import { useRef, useEffect, useState } from 'react';
import { embedDashboard } from '@superset-ui/embedded-sdk';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useDashboardConfig, useGuestTokenFetcher } from '../../hooks/useDashboard';
import { CONFIG } from '../../config';
import css from './DashboardPage.module.css';

const SUPERSET_DOMAIN = CONFIG.SUPERSET_DOMAIN;
const DASHBOARD_ID = '1'; // fallback numeric ID used to fetch config

export default function DashboardPage() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [embedError, setEmbedError] = useState<string | null>(null);
  const [embedded, setEmbedded] = useState(false);

  // 1. Fetch the embed UUID from the backend config endpoint
  const {
    data: config,
    isLoading: configLoading,
    error: configError,
  } = useDashboardConfig(DASHBOARD_ID);

  const embedId = config?.embedUuid;

  // 2. Provide a token-fetcher the SDK can call on init + refresh
  const fetchGuestToken = useGuestTokenFetcher(embedId);

  // 3. Embed the dashboard once we have the UUID + DOM node
  useEffect(() => {
    if (!embedId || !containerRef.current) return;

    let cancelled = false;
    const mountPoint = containerRef.current;

    (async () => {
      try {
        // Clear any previous embed
        mountPoint.innerHTML = '';
        setEmbedError(null);

        await embedDashboard({
          id: embedId,
          supersetDomain: SUPERSET_DOMAIN,
          mountPoint,
          fetchGuestToken,
          dashboardUiConfig: {
            hideTitle: false,
            hideChartControls: false,
            hideTab: false,
            filters: {
              visible: true,
              expanded: false,
            },
          },
        });

        if (!cancelled) {
          setEmbedded(true);

          // Ensure iframe fills the container (mirrors legacy forceFill logic)
          const applyFill = () => {
            const els = mountPoint.querySelectorAll<HTMLElement>('iframe, *');
            els.forEach((el) => {
              try {
                el.style.width = '100%';
                el.style.height = '100%';
                el.style.minHeight = '100%';
                el.style.border = '0';
                el.style.display = 'block';
              } catch {
                /* skip non-styleable nodes */
              }
            });
          };

          applyFill();
          // Re-apply on container resize
          if (window.ResizeObserver) {
            const ro = new ResizeObserver(() => applyFill());
            ro.observe(mountPoint);
            // Cleanup observer when effect re-runs
            return () => ro.disconnect();
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error embedding Superset dashboard:', err);
          setEmbedError(err instanceof Error ? err.message : 'Failed to load dashboard');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [embedId, fetchGuestToken]);

  /* ---------- render ---------- */

  const isLoading = configLoading || (!embedded && !embedError && !!embedId);
  const errorMsg =
    configError instanceof Error
      ? configError.message
      : (embedError ?? (configError ? 'Failed to load config' : null));

  return (
    <div className={css.wrapper}>
      <div className={css.container}>
        {/* Loading overlay */}
        {isLoading && (
          <div className={css.loading}>
            <FontAwesomeIcon icon={faSpinner} spin className={css.loadingIcon} />
            <p>Loading dashboard…</p>
          </div>
        )}

        {/* Error state */}
        {errorMsg && (
          <div className={css.error}>
            <FontAwesomeIcon icon={faExclamationTriangle} className={css.errorIcon} />
            <p className={css.errorTitle}>Failed to load dashboard</p>
            <p className={css.errorDetail}>{errorMsg}</p>
          </div>
        )}

        {/* Superset iframe mount point */}
        <div ref={containerRef} className={css.embed} />
      </div>
    </div>
  );
}
